import { type App, type Plugin, Route, View } from "@pimo/pimo-app-builder";
import {
  ArtefactsProgressCardV2,
  DerivePimoTableProps,
  DoubleNumberCardV2,
  GenericStatusCardV2,
  GridLayout,
  GridLayoutProps,
  NumberCardV2,
  PimoTable,
  PimoTableEventNames,
  PimoTableEventPayload,
  PimoTableFilterEventPayload,
  TableTextCell,
} from "@pimo/pimo-components";
import { InitiativeFilterData, OverviewFilterData, Phase } from "aint-types";
import { getColorsAndTextForInitiativePhaseChips, IN_COLORS } from "aint-utils";
import { generatePath } from "react-router-dom";

import { AintStatusesCell } from "../../components/aint-initiatives-table/aint-statuses-cell.tsx";
import { PhaseSectionsCard } from "../../components/aint-initiatives-table/phase-sections-card.tsx";
import { OverviewTitleCard } from "../../components/overview-title-card/overview-title-card.tsx";
import { FORM_FIELD_INFO_TEXT } from "../../form/form-field-info-text.ts";
import { InAppState } from "../app";
import { APP_ROUTES } from "../constants";
import { fetchAllRegions, fetchOEOverview } from "../helpers/fetch-helpers.ts";
import { theme } from "../theme.ts";
import { convertNumberToMillions } from "../utils/value-converters.ts";

export class OverviewPlugin implements Plugin<InAppState, InAppState> {
  route?: Route;
  private view?: View<InAppState, GridLayout>;
  private app?: App<InAppState>;

  onRegister(app: App<InAppState>): void {
    this.app = app;
    this.view = app.createView({
      name: "Overview",
      layout: new GridLayout(2, false, 3),
    });
    this.buildRoute();
    this.buildPage();
  }

  private buildPage() {
    if (!this.view) {
      return;
    }

    const overviewTitleCard = this.view.addComponent({
      component: OverviewTitleCard,
      layoutProps: { xs: 12 },
    });

    overviewTitleCard.mapState((appState) => {
      const filterData = appState.overviewFilterData;
      const filterOptions = appState.overviewFilterOptions;
      return {
        title: "OE Overview",
        filterData: filterData,
        filterOptions: filterOptions,
        cardProps: { sx: { width: "100%" } },
      };
    });

    overviewTitleCard.on("filter:apply", async ({ payload }) => {
      if (!this.app) return;

      const oeOverview = await fetchOEOverview(payload);

      this.app.patchAppState({
        overviewFilterData: payload,
        oeOverview,
      });
    });

    overviewTitleCard.on("filter:clear", async () => {
      if (!this.app) return;

      const oeOverview = await fetchOEOverview({} as OverviewFilterData);

      this.app.patchAppState({
        overviewFilterData: {
          oeNameFilter: [],
          regionsFilter: [],
          searchQueryFilter: "",
        },
        oeOverview,
      });
    });

    const totalInitiatives = this.view.addComponent({
      component: NumberCardV2,
      layoutProps: {
        xs: 12,
        md: 12 / 2,
        lg: 12 / 4,
      },
    });

    totalInitiatives.mapState((state) => {
      return {
        cardProps: {
          sx: {
            border: "1.5px solid",
            borderColor: theme.palette.primary.main,
          },
        },
        title: "Total Initiatives",

        number: state.oeOverview?.numberOfInitiatives ?? 0,
        titleColor: theme.palette.primary.contrastText,
        valueTextColor: theme.palette.primary.contrastText,
        background: "white",
      };
    });
    const totalNetBenefitsCard = this.view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        md: 12 / 2,
        lg: 12 / 4,
      },
    });

    totalNetBenefitsCard.mapState((state) => {
      return {
        title: "Total Net Benefits",
        infoBoxText: FORM_FIELD_INFO_TEXT["totalNetBenefits"],
        textColor: theme.palette.primary.main,
        content1: {
          value: `${
            convertNumberToMillions(
              state.oeOverview?.totalNetBenefits?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn EUR`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              state.oeOverview?.totalNetBenefits?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn EUR`,
          label: "Planned",
        },
      };
    });

    const overallProgressCard = this.view.addComponent({
      component: ArtefactsProgressCardV2,
      layoutProps: {
        xs: 12,
        md: 12 / 2,
        lg: 12 / 4,
        viewname: "initiatives",
      } as GridLayoutProps,
    });

    overallProgressCard.mapState((appState) => {
      const completedOe =
        Number(
          appState.oeOverview?.oes?.filter(
            (oe) => oe.oeUpdateStatus === "completed"
          ).length ?? 0
        ) ?? 0;
      const totalNumberOfOEs = (appState.oeOverview?.oes ?? []).length ?? 1;
      return {
        cardIcon: "bars.svg",
        cardTitle: "OE Monthly Reporting Progress",

        progressBarProps: {
          color: IN_COLORS.lightBlue,
          displayValue: `${completedOe}/${totalNumberOfOEs} (${Math.round(
            (completedOe / totalNumberOfOEs) * 100
          )} %)`,
          value: Math.round((completedOe / totalNumberOfOEs) * 100),
          label: "Reporting Completed",
        },
        titleProps: { color: theme.palette.primary.contrastText },
      };
    });

    const updatesCompletedCard = this.view.addComponent({
      component: GenericStatusCardV2,
      layoutProps: {
        xs: 12,
        md: 12 / 2,
        lg: 12 / 4,
        viewname: "initiatives",
      } as GridLayoutProps,
    });

    updatesCompletedCard.mapState((state) => {
      return {
        title: "OE Monthly Reporting Status",
        cardIcon: "checklist.svg",
        statuses: [
          {
            status: "red",
            name: "Overdue",
            count: Number(
              (state.oeOverview?.oes ?? []).filter(
                (oe) => oe.oeUpdateStatus === "overdue"
              ).length ?? 0
            ),
            imageUrl: "mood-sad.svg",
          },
          {
            status: "yellow",
            name: "Due",
            count: Number(
              (state.oeOverview?.oes ?? []).filter(
                (oe) => oe.oeUpdateStatus === "pending"
              ).length ?? 0
            ),
            imageUrl: "mood-neutral.svg",
          },
          {
            status: "green",
            name: "Completed",
            count: Number(
              (state.oeOverview?.oes ?? []).filter(
                (oe) => oe.oeUpdateStatus === "completed"
              ).length ?? 0
            ),
            imageUrl: "mood-smile.svg",
          },
        ],
      };
    });

    const statusTableDefinition = [
      { component: TableTextCell },
      { component: PhaseSectionsCard },
      { component: TableTextCell },
      { component: TableTextCell },
      { component: AintStatusesCell },
    ] as const;

    const statusTable = new PimoTable(statusTableDefinition);

    const initiativesStatusTable = this.view.addComponent<
      DerivePimoTableProps<typeof statusTable>,
      PimoTableEventNames,
      PimoTableEventPayload
    >({
      component: statusTable,
      layoutProps: {
        viewname: "initiatives",
        xs: 12,
      } as GridLayoutProps,
    });

    initiativesStatusTable.on("filter", (event) => {
      const payload =
        event?.payload as PimoTableFilterEventPayload<InitiativeFilterData>;

      this.app?.patchAppState({
        initiativeFilterData: {
          searchQueryFilter: payload?.filter?.searchQueryFilter ?? "",
          yearFilter: payload?.filter?.yearFilter ?? "",
          phaseFilter: payload?.filter?.phaseFilter ?? [],
          updateStatusFilter: payload?.filter?.updateStatusFilter ?? [],
        },
      });
    });

    initiativesStatusTable.mapState((state) => {
      return {
        data:
          state.oeOverview?.oes?.map((oe) => {
            return {
              columnProps: [
                {
                  body: oe.oeName,
                  bodyProps: {
                    sx: {
                      fontWeight: 400,
                    },
                  },
                },
                {
                  phases: Object.entries(oe.phasesOverview).map(
                    ([phase, count]) => ({
                      count,
                      phaseName: getColorsAndTextForInitiativePhaseChips(
                        phase as unknown as Phase
                      ).label,
                      phaseColor: getColorsAndTextForInitiativePhaseChips(
                        phase as unknown as Phase
                      ).chipColor,
                      textColor: getColorsAndTextForInitiativePhaseChips(
                        phase as unknown as Phase
                      ).textColor,
                    })
                  ),
                },
                {
                  body: `${convertNumberToMillions(oe.netBenefitAcutal)?.toLocaleString("en-US") ?? ""} Mn €`,
                  containerProps: {
                    sx: { alignItems: "right", justifyContent: "right" },
                  },
                  tooltipShow: true,
                  tooltipTitle: FORM_FIELD_INFO_TEXT.actualNetBenefits,
                },
                {
                  body: `${convertNumberToMillions(oe.netBenefitPlan)?.toLocaleString("en-US") ?? ""} Mn €`,
                  containerProps: {
                    sx: { alignItems: "right", justifyContent: "right" },
                  },
                  tooltipTitle: FORM_FIELD_INFO_TEXT.plannedNetBenefits,
                  tooltipShow: true,
                },
                {
                  statuses: [
                    {
                      chipColor: "#CD0A0E",
                      textColor: "white",
                      value: oe.statusCounts.overdue.toString(),
                      label: "Overdue",
                    },
                    {
                      chipColor: "#FAB600",
                      textColor: "white",
                      value: oe.statusCounts.pending.toString(),
                      label: "Due",
                    },
                    {
                      chipColor: "#087C4C",
                      textColor: "white",
                      value: oe.statusCounts.completed.toString(),
                      label: "Completed",
                    },
                  ],
                },
              ],
              rowProps: {
                onClick: () => {
                  this.app?.navigate(
                    generatePath(APP_ROUTES.operatingEntities, {
                      oeId: String(oe.id),
                      viewname: "initiatives",
                    })
                  );
                },
              },
            };
          }) ?? [],
        tableHeaderEntries: [
          {
            fieldName: "oeName",
            label: "OE",
            sort: null,
          },
          {
            fieldName: "phases",
            label: "Total Initiatives",
            sort: null,
          },
          {
            fieldName: "totalNetBenefits.actual",
            label: "Net Benefits (Actual)",
            sort: null,
            sx: {
              justifyContent: "flex-end",
            },
          },
          {
            fieldName: "totalNetBenefits.planned",
            label: "Net Benefits (Plan)",
            sort: null,
            sx: {
              justifyContent: "flex-end",
            },
          },
          {
            fieldName: "status",
            label: "Initiative Update Status",
            sort: null,
          },
        ],
      };
    });
  }

  private buildRoute() {
    if (!this.app || !this.view) {
      return;
    }

    this.route = this.app.createRoute({
      path: `${APP_ROUTES.overview}`,
      view: this.view,
    });

    this.route?.on("load", async () => {
      if (!this.app) {
        return;
      }

      const [oeOverview, allRegions] = await Promise.all([
        fetchOEOverview(),
        fetchAllRegions(),
      ]);

      this.app.patchAppState({
        oeOverview,

        overviewFilterData: {
          oeNameFilter: [],
          regionsFilter: [],
          searchQueryFilter: "",
        },
        overviewFilterOptions: {
          oeName: oeOverview.oes.map((oe) => oe.oeName),
          regions: allRegions.map(({ regionName }) => regionName),
        },
        isLoading: false,
      });
    });
  }
}
